import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Avatar,
    Chip,
    Rating,
    Grid,
    Button,
    Divider,
    Tab,
    Tabs,
    CircularProgress,
    Stack,
    Fade,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    useTheme,
    alpha
} from '@mui/material';
import {
    Close as CloseIcon,
    Verified as VerifiedIcon,
    DirectionsCar as CarIcon,
    EventSeat as SeatIcon,
    Star as StarIcon,
    Person as PersonIcon,
    DriveEta as DriverIcon,
    CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import apiService from '../services/apiService';

const UserProfileModal = ({ userId, isOpen, onClose }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedReviews, setExpandedReviews] = useState(false);

    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('[UserProfileModal] useEffect triggered', { isOpen, userId });
        if (isOpen && userId) {
            setError(null);
            loadUserProfile();
        } else {
            console.log('[UserProfileModal] Clearing data');
            setUserData(null);
            setLoading(true);
            setError(null);
        }
    }, [isOpen, userId]);

    const loadUserProfile = async () => {
        console.log('[UserProfileModal] Starting loadUserProfile for:', userId);
        setLoading(true);
        setError(null);
        try {
            const endpoint = `/users/${userId}`;
            console.log('[UserProfileModal] Calling API:', endpoint);
            const response = await apiService.get(endpoint);
            console.log('[UserProfileModal] API Response received:', response);
            if (response.success) {
                setUserData(response.data.user);
            } else {
                throw new Error(response.message || 'API returned success: false');
            }
        } catch (err) {
            console.error('[UserProfileModal] Failed to load user profile', err);
            setError(err);
        } finally {
            setLoading(false);
            console.log('[UserProfileModal] loadUserProfile finished');
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (!isOpen) return null;

    // Safe data transformation
    const reviews = (userData?.reviews || []).map(r => {
        try {
            const dateObj = r.createdAt ? new Date(r.createdAt) : null;
            const dateStr = (dateObj && !isNaN(dateObj.getTime()))
                ? dateObj.toLocaleDateString()
                : 'Unknown Date';

            return {
                id: r.id || Math.random().toString(),
                user: r.fromUser ? `${r.fromUser.preferredName || r.fromUser.firstName || ''} ${r.fromUser.lastName ? r.fromUser.lastName.charAt(0) + '.' : ''}`.trim() || 'User' : 'Unknown',
                date: dateStr,
                rating: r.rating || 0,
                comment: r.comment || '',
                avatar: r.fromUser?.profilePicture
            };
        } catch (e) {
            console.error('Error transforming review:', e, r);
            return null;
        }
    }).filter(Boolean);

    const displayedReviews = expandedReviews ? reviews : reviews.slice(0, 5);

    const glassStyle = {
        background: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.8)
            : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    ...glassStyle
                }
            }}
        >
            {loading ? (
                <Box sx={{ p: 8, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : userData ? (
                <>
                    {/* Header Banner */}
                    <Box sx={{
                        height: 140,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        position: 'relative'
                    }}>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.2)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.3)' }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Profile Content */}
                    <DialogContent sx={{ mt: -7, px: 3, pb: 4 }}>
                        {/* Avatar & Main Info */}
                        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                            <Avatar
                                src={userData.profilePicture}
                                sx={{
                                    width: 120,
                                    height: 120,
                                    border: '4px solid white',
                                    boxShadow: theme.shadows[4],
                                    bgcolor: theme.palette.primary.light,
                                    fontSize: '3rem'
                                }}
                            >
                                {userData.firstName?.[0] || '?'}
                            </Avatar>

                            <Box display="flex" alignItems="center" gap={1} mt={2}>
                                <Typography variant="h5" fontWeight="800">
                                    {userData.preferredName || userData.firstName} {userData.lastName ? userData.lastName.charAt(0) + '.' : ''}
                                </Typography>
                                {userData.isLicenseVerified && (
                                    <VerifiedIcon color="primary" />
                                )}
                            </Box>

                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                <Rating value={userData.driverRating || 0} readOnly precision={0.1} size="small" />
                                <Typography variant="body2" color="text.secondary" fontWeight="600">
                                    {userData.driverRating ? userData.driverRating.toFixed(1) : 'New'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    ({userData.totalTripsAsDriver || 0} reviews)
                                </Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Member since {(() => {
                                    try {
                                        const d = new Date(userData.createdAt);
                                        return !isNaN(d.getTime()) ? d.getFullYear() : '...';
                                    } catch { return '...'; }
                                })()}
                            </Typography>
                        </Box>

                        {/* Stats Grid */}
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            <Grid size={{ xs: 6 }}>
                                <Box sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    textAlign: 'center',
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                }}>
                                    <DriverIcon color="primary" sx={{ fontSize: 32, mb: 1, opacity: 0.8 }} />
                                    <Typography variant="h6" fontWeight="800" color="primary">
                                        {userData.totalTripsAsDriver || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase">
                                        Rides Given
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Box sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    bgcolor: alpha(theme.palette.secondary.main, 0.05),
                                    textAlign: 'center',
                                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
                                }}>
                                    <SeatIcon color="secondary" sx={{ fontSize: 32, mb: 1, opacity: 0.8 }} />
                                    <Typography variant="h6" fontWeight="800" color="secondary">
                                        {userData.totalTripsAsPassenger || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase">
                                        Rides Joined
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Tabs */}
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            sx={{
                                mb: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                '& .MuiTab-root': { fontWeight: 700 }
                            }}
                        >
                            <Tab label="About" />
                            <Tab label={`Reviews (${reviews.length})`} />
                        </Tabs>

                        {/* About Tab */}
                        {activeTab === 0 && (
                            <Fade in>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        About
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        {userData.bio || "No bio available."}
                                    </Typography>

                                    {/* Vehicles */}
                                    {userData.vehicles && userData.vehicles.length > 0 && (
                                        <>
                                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 2 }}>
                                                Vehicles
                                            </Typography>
                                            <Stack spacing={2}>
                                                {userData.vehicles.map((car, index) => (
                                                    <Box key={index} sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
                                                    }}>
                                                        <Avatar sx={{ bgcolor: theme.palette.grey[300] }}>
                                                            <CarIcon color="action" />
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body1" fontWeight="600">
                                                                {car.color} {car.make?.name || car.make} {car.model?.name || car.model}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {car.year} • {car.licensePlate ? 'License verified' : 'No license info'}
                                                            </Typography>
                                                        </Box>
                                                        {car.status === 'APPROVED' && (
                                                            <Chip label="Verified" size="small" color="success" sx={{ ml: 'auto' }} />
                                                        )}
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </>
                                    )}
                                </Box>
                            </Fade>
                        )}

                        {/* Reviews Tab */}
                        {activeTab === 1 && (
                            <Fade in>
                                <Box>
                                    <List disablePadding>
                                        {displayedReviews.map((review) => (
                                            <React.Fragment key={review.id}>
                                                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            src={review.avatar}
                                                            sx={{ width: 40, height: 40, bgcolor: theme.palette.secondary.light }}
                                                        >
                                                            {review.user.charAt(0)}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                <Typography variant="subtitle2" fontWeight="bold">{review.user}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{review.date}</Typography>
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box mt={0.5}>
                                                                <Rating value={review.rating} readOnly size="small" sx={{ fontSize: '0.9rem', mb: 0.5 }} />
                                                                <Typography variant="body2" color="text.primary">
                                                                    "{review.comment}"
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                                <Divider component="li" variant="inset" />
                                            </React.Fragment>
                                        ))}
                                    </List>

                                    {!expandedReviews && reviews.length > 5 && (
                                        <Button
                                            fullWidth
                                            sx={{ mt: 2 }}
                                            onClick={() => setExpandedReviews(true)}
                                        >
                                            Show More Reviews
                                        </Button>
                                    )}
                                </Box>
                            </Fade>
                        )}

                    </DialogContent>
                </>
            ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('profileUnavailable', 'User profile unavailable')}
                    </Typography>
                    {error && (
                        <Typography variant="caption" color="error" display="block" sx={{ mb: 2, p: 1, bgcolor: '#fff0f0', borderRadius: 1 }}>
                            Debug: {error.message || 'Unknown Error'} <br /> (User ID: {userId})
                        </Typography>
                    )}
                    <Button onClick={onClose} variant="outlined" sx={{ mt: 2 }}>
                        {t('close', 'Close')}
                    </Button>
                </Box>
            )}
        </Dialog>
    );
};

export default UserProfileModal;
